import { addMinutes, addSeconds, max, min, roundToNearestMinutes } from 'date-fns';
import { clamp, last, sortedIndex, sortedUniq } from 'lodash';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { InteractionSource } from '@/charts/types';

export type ActiveTimeline = { timestamps: string[]; subsample: boolean };

export type KeyboardInteractionHandler = (source: InteractionSource) => void;

export type TimelineNavigationContextType = {
    selectedTimestamp: string | null;
    setSelectedTimestamp: (selectedTimestamp: string | null, timeline?: string[]) => void;
    selectedSubsampleTimestamp: string | null;
    setSelectedSubsampleTimestamp: (selectedTimestamp: string | null, timeline?: string[]) => void;
    activeTimeline: ActiveTimeline | null;
    setActiveTimeline: (activeTimeline: { timestamps: string[]; subsample?: boolean } | null) => void;
    setKeyboardInteractionHandler: (handler: KeyboardInteractionHandler) => void;
};

/**
 * Outside a provider there is no timeline cursor: nothing selected; setters are inert rather than
 * absent so consumers no-op instead of crashing.
 */
const NO_TIMELINE_NAVIGATION: TimelineNavigationContextType = {
    selectedTimestamp: null,
    setSelectedTimestamp: () => {},
    selectedSubsampleTimestamp: null,
    setSelectedSubsampleTimestamp: () => {},
    activeTimeline: null,
    setActiveTimeline: () => {},
    setKeyboardInteractionHandler: () => {},
};

const SUBSAMPLE_STEP_SECONDS = 5;
const SUBSAMPLE_SHIFT_STEP_MINUTES = 1;
const TIMELINE_STEP_MINUTES = 1;
const TIMELINE_SHIFT_STEP_MINUTES = 15;

const roundToNearestSeconds = (date: Date, nearestToSeconds: number): Date => {
    const intervalMilliseconds = nearestToSeconds * 1000;
    return new Date(Math.round(date.getTime() / intervalMilliseconds) * intervalMilliseconds);
};

const stepTimestamp = (
    from: string,
    { direction, subsample, shift }: { direction: 1 | -1; subsample: boolean; shift: boolean }
): Date => {
    if (subsample) {
        return shift
            ? roundToNearestMinutes(addMinutes(from, direction * SUBSAMPLE_SHIFT_STEP_MINUTES), {
                  nearestTo: SUBSAMPLE_SHIFT_STEP_MINUTES,
                  roundingMethod: 'round',
              })
            : roundToNearestSeconds(addSeconds(from, direction * SUBSAMPLE_STEP_SECONDS), SUBSAMPLE_STEP_SECONDS);
    }
    return shift
        ? roundToNearestMinutes(addMinutes(from, direction * TIMELINE_SHIFT_STEP_MINUTES), {
              nearestTo: TIMELINE_SHIFT_STEP_MINUTES,
              roundingMethod: 'round',
          })
        : roundToNearestMinutes(addMinutes(from, direction * TIMELINE_STEP_MINUTES), { roundingMethod: 'floor' });
};

const TimelineNavigationContext = createContext(NO_TIMELINE_NAVIGATION);

const TimelineNavigationContextProvider = ({ children }: { children: ReactNode }) => {
    const [selectedTimestamp, setSelectedTimestampValue] = useState<string | null>(null);
    const [selectedSubsampleTimestamp, setSelectedSubsampleTimestampValue] = useState<string | null>(null);
    const [activeTimeline, setActiveTimelineValue] = useState<ActiveTimeline | null>(null);

    // The handler of the chart that was "selected" by the user last.
    const keyboardInteractionHandler = useRef<KeyboardInteractionHandler | null>(null);

    const setKeyboardInteractionHandler = useCallback((handler: KeyboardInteractionHandler) => {
        keyboardInteractionHandler.current = handler;
    }, []);

    /*
     * These setters are used in the dependency array of effects that render charts, so they need to have
     * very stable references to avoid unnecessary redraws.
     */
    const setSelectedTimestamp = useCallback((selectedTimestamp: string | null, timeline?: string[]) => {
        setSelectedTimestampValue(selectedTimestamp);
        setSelectedSubsampleTimestampValue(null);
        if (timeline) {
            keyboardInteractionHandler.current = null;
            setActiveTimelineValue({ timestamps: sortedUniq(timeline.toSorted()), subsample: false });
        }
    }, []);

    const setSelectedSubsampleTimestamp = useCallback(
        (selectedSubsampleTimestamp: string | null, timeline?: string[]) => {
            setSelectedSubsampleTimestampValue(selectedSubsampleTimestamp);
            if (timeline) {
                keyboardInteractionHandler.current = null;
                setActiveTimelineValue({ timestamps: sortedUniq(timeline.toSorted()), subsample: true });
            }
        },
        []
    );

    const setActiveTimeline = useCallback((activeTimeline: { timestamps: string[]; subsample?: boolean } | null) => {
        setActiveTimelineValue(
            activeTimeline
                ? {
                      subsample: activeTimeline.subsample ?? false,
                      timestamps: sortedUniq(activeTimeline.timestamps.toSorted()),
                  }
                : null
        );
    }, []);

    /*
     * Arrow-key navigation needs the current cursor at the time of keydown, but does not need to react.
     * We do not want to re-register the listener whenever the selection moves, so just track the latest
     * in a ref.
     */
    const navigationState = useRef({ selectedTimestamp, selectedSubsampleTimestamp, activeTimeline });
    navigationState.current = { selectedTimestamp, selectedSubsampleTimestamp, activeTimeline };

    useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            const { selectedTimestamp, selectedSubsampleTimestamp, activeTimeline } = navigationState.current;

            if (event.metaKey) {
                return;
            }

            if (!activeTimeline || !selectedTimestamp) {
                return;
            }

            const { timestamps, subsample } = activeTimeline;
            const [selected, setSelectedValue] = subsample
                ? [selectedSubsampleTimestamp, setSelectedSubsampleTimestamp]
                : [selectedTimestamp, setSelectedTimestamp];

            // A held key repeats `keydown`. Report the first press only.
            const setSelected = (timestamp: string) => {
                setSelectedValue(timestamp);
                if (!event.repeat) {
                    keyboardInteractionHandler.current?.('keyboard');
                }
            };

            /*
             * Escape releases the innermost cursor, so on a subsample timeline the first press drops the
             * subsample cursor and a second press drops the top-level one.
             */
            if (event.key === 'Escape') {
                if (subsample && selectedSubsampleTimestamp) {
                    setSelectedSubsampleTimestamp(null);
                } else {
                    setSelectedTimestamp(null);
                }
                return;
            }

            if (event.ctrlKey || event.altKey) {
                let targetTimestamp: Date | string | undefined;
                if (event.key === 'ArrowLeft') {
                    targetTimestamp = selected
                        ? stepTimestamp(selected, { direction: -1, subsample, shift: event.shiftKey })
                        : last(timestamps);
                } else if (event.key === 'ArrowRight') {
                    targetTimestamp = selected
                        ? stepTimestamp(selected, { direction: 1, subsample, shift: event.shiftKey })
                        : timestamps[0];
                }
                if (targetTimestamp && timestamps.length) {
                    setSelected(min([last(timestamps)!, max([timestamps[0], targetTimestamp])]).toISOString());
                }
            } else {
                const selectedIndex = sortedIndex(timestamps, selected);
                let targetIndex = null;

                if (event.key === 'ArrowLeft') {
                    targetIndex = event.shiftKey ? selectedIndex - 10 : selectedIndex - 1;
                } else if (event.key === 'ArrowRight') {
                    if (selected === timestamps[selectedIndex]) {
                        // exact match, so forward one is selectedIndex + 1
                        targetIndex = event.shiftKey ? selectedIndex + 10 : selectedIndex + 1;
                    } else {
                        // selection was between selectedIndex and one before it, so selectedIndex is next
                        targetIndex = event.shiftKey ? selectedIndex + 9 : selectedIndex;
                    }
                }

                if (targetIndex !== null) {
                    setSelected(timestamps[clamp(targetIndex, 0, timestamps.length - 1)]);
                }
            }
        }

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [setSelectedSubsampleTimestamp, setSelectedTimestamp]);

    const contextValue: TimelineNavigationContextType = useMemo(
        () => ({
            selectedTimestamp,
            setSelectedTimestamp,
            selectedSubsampleTimestamp,
            setSelectedSubsampleTimestamp,
            activeTimeline,
            setActiveTimeline,
            setKeyboardInteractionHandler,
        }),
        [
            activeTimeline,
            selectedSubsampleTimestamp,
            selectedTimestamp,
            setActiveTimeline,
            setKeyboardInteractionHandler,
            setSelectedSubsampleTimestamp,
            setSelectedTimestamp,
        ]
    );

    return <TimelineNavigationContext.Provider value={contextValue}>{children}</TimelineNavigationContext.Provider>;
};

const useTimelineNavigationContext = () => useContext(TimelineNavigationContext);

export { TimelineNavigationContextProvider, useTimelineNavigationContext };
